import LocaleWrapper from '../entities/locale-wrapper.js';
import { DAY, HOUR, MINUTE, SECOND } from '../constants/date.js';

/**
 * @param date - Process date
 * @param format - Format for month name ('March', 'Mar', '03')
 * @param locale - Format language
 * @returns The month name for the given date in the selected format
 * */
function getMonthName(date, format = 'long', locale = 'ru') {
  return new Intl.DateTimeFormat(locale, {
    month: format
  }).format(date);
}
/**
 * @param date - Process date
 * @param format - Format for weekday
 * @param locale - Format language
 * @returns The weekday name for given date in seleectd format
 */
function getWeekdayName(date, format = 'long', locale = 'ru') {
  return new Intl.DateTimeFormat(locale, {
    weekday: format
  }).format(date);
}
/**
 * Function for working with Intl DateTimeFormat
 * @param date - Process date
 * @param options - Options for Intl DateTimeFormat formatter
 * @param [locale='ru']
 * @returns Object with date parts (year, month, etc.)
 */
function getDateParts(date, options = {}, locale = 'ru') {
  const parts = new Intl.DateTimeFormat(locale, options).formatToParts(date);
  return parts.reduce((acc, item) => {
    const key = item.type;
    acc[key] = item.value;
    return acc;
  }, {});
}
function isLessThenYesterday(date) {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date < yesterday;
}
function addZero(value) {
  return `${value}`.padStart(2, '0');
}
function date02format(value) {
  if (Number.isInteger(value)) {
    if (value < 10 && value >= 0) return `0${value}`;
    if (value < 0 && value > -10) return `-0${`${value}`.substring(1)}`;
  }
  return `${value}`;
}
function getTimeInSeconds() {
  return Math.floor(Date.now() / 1000);
}
function getFirstDayInMonth(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month, 1, 0, 0, 0, 0);
}
function getLastDayInMonth(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return new Date(year, month, 0, 0, 0, 0, 0);
}
function getMonthDateRange(date = new Date()) {
  const from = getFirstDayInMonth(date);
  const to = getLastDayInMonth(date);
  return {
    from,
    to
  };
}
function timeFormat(date) {
  return `${addZero(date.getHours())}:${addZero(date.getMinutes())}`;
}
/**
 * @param dateString - Date in string format
 * @param options
 * @returns Date object
 */
function stringToDate(dateString, options = {}) {
  const splitted = dateString.split(options.sep || ' ');
  const datePart = splitted[0];
  const timePart = splitted[1] || '00:00';
  const [hoursPart, minutesPart] = timePart.split(':');
  const [yearPart, monthPart, dayPart] = datePart.split('-');
  const year = Number(yearPart);
  const month = Number(monthPart) - 1;
  const day = Number(dayPart);
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  return new Date(year, month, day, hours, minutes);
}
/**
 * @param date - Date to process
 * @param format - String like 'DD.MM.YYYY'
 * @returns Date in string format
 */
function dateToString(date, format = 'DD.MM.YYYY') {
  if (!(date instanceof Date)) return '';
  const year = date.getFullYear();
  const month = addZero(date.getMonth() + 1);
  const _date = addZero(date.getDate());
  const hours = addZero(date.getHours());
  const minutes = addZero(date.getMinutes());
  const seconds = addZero(date.getSeconds());
  return format.replace(/DD/, _date).replace(/MM/, month).replace(/YYYY/, `${year}`).replace(/(HH|hh)/, hours).replace(/mm/, minutes).replace(/ss/, seconds);
}
function secondsToPrettyString(seconds = 0) {
  const milliseconds = seconds * 1000;
  const days = Math.floor(milliseconds / DAY);
  const hours = Math.floor(milliseconds % DAY / HOUR);
  const minutes = Math.floor(milliseconds % HOUR / MINUTE);
  const _seconds = Math.floor(milliseconds % MINUTE / SECOND);
  let result = '';
  result += days > 0 ? `${addZero(days)}:` : '';
  result += hours > 0 ? `${addZero(hours)}:` : '';
  result += minutes >= 0 ? `${addZero(minutes)}:` : '';
  result += `${addZero(_seconds)}`;
  return result;
}
function unixToString(unixTime) {
  const h = Math.floor(unixTime / 3600);
  const m = Math.floor(unixTime / 60 - h * 60);
  const fixedMin = m.toString().length === 1 ? `0${m}` : m;
  if (h === 0) return `${fixedMin} мин`;
  if (m === 0) return `${h}ч`;
  return `${h}ч ${fixedMin} мин`;
}
function isDate(value) {
  return value instanceof Date && Object.prototype.toString.call(value) === '[object Date]';
}
function isValidDate(value) {
  return isDate(value) || typeof value === 'string' && !isNaN(stringToDate(value).getTime());
}
function timezoneFormat(utc) {
  const string = `${date02format(utc)}:00`;
  if (utc > 0) return `+${string}`;
  return string;
}
function transformDateToObject(inputDate, options = {}) {
  const result = {
    year: '',
    month: '',
    date: '',
    day: '',
    hour: '',
    minute: '',
    second: '',
    weekday: '',
    time: ''
  };
  if (!isDate(inputDate) && typeof inputDate !== 'string') return result;
  const _options = Object.assign({
    formatterOptions: {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      weekday: 'long',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }
  }, options);
  const locale = _options.locale || LocaleWrapper.locale;
  const date = typeof inputDate === 'string' ? new Date(inputDate.replace(/-/g, '/')) : inputDate;
  const formatter = new Intl.DateTimeFormat(locale, _options.formatterOptions);
  const formattedParts = formatter.formatToParts(date);
  formattedParts.forEach(part => {
    if (!Object.hasOwn(result, part.type)) return;
    result[part.type] = part.value;
  });
  result.date = `${date.getDate()}`;
  result.day = `${date.getDay()}`;
  result.time = timeFormat(date);
  return result;
}
function dateToDateObject(
// dateToObj
date, options = {}) {
  const result = {
    date: '',
    month: {
      value: '',
      label: ''
    },
    year: NaN,
    isValid: false
  };
  if (!date) return result;
  const locale = (options === null || options === void 0 ? void 0 : options.locale) || LocaleWrapper.locale;
  const _date = typeof date === 'string' ? stringToDate(date) : date;
  result.year = _date.getFullYear();
  result.date = addZero(_date.getDate());
  result.month.value = addZero(_date.getMonth() + 1);
  result.month.label = getMonthName(_date, 'long', locale);
  result.isValid = true;
  return result;
}
function dateObjectToDate(dateObject) {
  if (!dateObject || !dateObject.year) return null;
  const {
    year,
    month,
    date
  } = dateObject;
  return new Date(year, Number(month.value) - 1, Number(date), 0, 0);
}
function dateObjectToString(dateObject) {
  if (!dateObject.year || !dateObject.month.value || !dateObject.date) return '';
  return `${dateObject.year}-${dateObject.month.value}-${dateObject.date}`;
}
function stringToDateObject(string) {
  const result = {
    date: '',
    month: {
      value: '',
      label: ''
    },
    year: NaN,
    isValid: false
  };
  if (!string || !string.length) return result;
  const date = new Date(string);
  result.date = addZero(date.getDate());
  result.month.value = addZero(date.getMonth() + 1);
  result.month.label = getMonthName(date, 'long');
  result.year = date.getFullYear();
  result.isValid = true;
  return result;
}

export { addZero, date02format, dateObjectToDate, dateObjectToString, dateToDateObject, dateToString, getDateParts, getFirstDayInMonth, getLastDayInMonth, getMonthDateRange, getMonthName, getTimeInSeconds, getWeekdayName, isDate, isLessThenYesterday, isValidDate, secondsToPrettyString, stringToDate, stringToDateObject, timeFormat, timezoneFormat, transformDateToObject, unixToString };
